<template>
	<pageWithNav>
		<innerPage>
			<div slot="page">
				<div style="display: flex; flex-direction: row">
					<div>
						<h1>Workflows</h1>
						<h3 style="font-weight: bold; margin-bottom: 0px">Existing workflows you have access to</h3>
					</div>
					<div style="display: flex; margin-left: auto; align-items: flex-end">
						<el-pagination
							:pager-count="5"
							:page-sizes="[10, 20, 50]"
							@current-change="returnWorkflowSummary"
							@size-change="returnWorkflowSummary"
							background
							:page-size.sync="pageSize"
							layout="sizes, prev, pager, next"
							:total="totalRecords"
							:current-page.sync="currentPage"
							style="margin-right: 20px"
						>
						</el-pagination>
						<el-button
							id="wf-tour-new-workflow-btn"
							@click="
								() => {
									showCreateWorkflowModal = true;
								}
							"
							type="primary"
						>
							<i style="margin-right: 10px" class="icon-workflow"></i>New Workflow
						</el-button>
						<slideOutModal
							@close="
								() => {
									showCreateWorkflowModal = false;
								}
							"
							:visible="showCreateWorkflowModal"
						>
							<createUpdateWorkflowForm @operationComplete="workflowCreated" />
						</slideOutModal>
						<el-button
							style="margin-left: 10px"
							plain
							@click="
								() => {
									showImportWorkflowModal = true;
								}
							"
							type="primary"
						>
							<i style="margin-right: 10px" class="el-icon-upload2"></i>Import
						</el-button>
						<slideOutModal
							@close="
								() => {
									showImportWorkflowModal = false;
								}
							"
							:visible="showImportWorkflowModal"
						>
							<importWorkflowForm @operationComplete="workflowCreated" />
						</slideOutModal>
					</div>
				</div>
				<el-divider class="compressedDivider"></el-divider>
				<el-container>
					<el-aside style="width: 250px">
						<el-input
							clearable
							placeholder="Search by workflow name"
							prefix-icon="el-icon-search"
							v-model="workflowSearchQuery"
							style="margin-bottom: 20px; width: 90%; margin-top: 24px"
						>
						</el-input>
						<sideList :multiSelect="true" @event="sideListEvent" :options="filterOptions" />
					</el-aside>
					<el-container>
						<el-main style="padding: 0px">
							<workflowItemList
								@workflowUpdated="workflowUpdated"
								@itemRemoved="workflowDeleted"
								:filter="filters"
								:searchQuery="workflowSearchQuery"
								:list="availableWorkflows"
								:tourWorkflowId="tourWorkflowId"
							/>
						</el-main>
					</el-container>
				</el-container>
			</div>
		</innerPage>
		<workflowTour
			:visible.sync="workflowsTourVisible"
			:steps="workflowsTourSteps"
			@completed="onListTourCompleted"
			@skipped="onListTourSkipped"
			@ended="onListTourSkipped"
			@step-change="handleTourStepChange"
		/>
	</pageWithNav>
</template>

<script>
import pageWithNav from "../components/pageWithNav.vue";
import innerPage from "../components/innerPage.vue";
import sideList from "../components/sideList.vue";
import { httpHelper } from "../utils/httpHelper.js";
import workflowItemList from "../components/workflowItemList.vue";
import slideOutModal from "../components/slideOutModal.vue";
import importWorkflowForm from "../components/importWorkflowForm.vue";
import createUpdateWorkflowForm from "../components/createUpdateWorkflowForm.vue";
import workflowTour from "../components/workflowTour.vue";
export default {
	components: {
		pageWithNav,
		innerPage,
		sideList,
		workflowItemList,
		slideOutModal,
		importWorkflowForm,
		createUpdateWorkflowForm,
		workflowTour,
	},
	data() {
		return {
			pageSize: 10,
			totalRecords: 0,
			currentPage: 1,
			availableWorkflows: [],
			workflowSearchQuery: "",
			filters: [],
			showCreateWorkflowModal: false,
			showImportWorkflowModal: false,
			workflowsTourVisible: false,
			tourWorkflowId: null,
			filterOptions: [
				{ name: "Owned By Me" },
				{ name: "Shared With Me" },
				{ name: "Active" },
				{ name: "Inactive" },
				{ name: "Can Edit" },
				{ name: "Can View Only" },
			],
		};
	},
	computed: {
		workflowsTourSteps() {
			const isManual = localStorage.getItem("workflowTourManualStart") === "true";
			const steps = [];

			if (!isManual) {
				steps.push({
					target: "#wf-tour-global-start-btn",
					title: "Begin Your Tour",
					content: "Welcome! For future reference, you can always restart this tour by clicking the <b>Start Workflow Tour</b> button here. Click it now to begin!",
					placement: "bottom",
					advanceWhen: { type: "click_target", selector: "#wf-tour-global-start-btn" },
					preventTargetDefault: true
				});
			}

			steps.push(
				{
					target: "#wf-tour-new-workflow-btn",
					title: "Start building",
					content: "Click the <b>New Workflow</b> button to get started with your first automation.",
					placement: "bottom",
					advanceWhen: { type: "click_target", selector: "#wf-tour-new-workflow-btn" }
				},
				{
					target: "#wf-tour-workflow-name-input",
					title: "Name your workflow",
					content: "Enter a descriptive name for your automation, then click <b>Next</b> on this card.",
					placement: "right",
					waitForTarget: true,
					manualAdvance: true
				},
				{
					target: "#wf-tour-workflow-create-btn",
					title: "Finalize Setup",
					content: "Great! Now click the <b>Create</b> button to save your workflow settings.",
					placement: "right",
					waitForTarget: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-workflow-create-btn" }
				},
				{
					target: "#wf-tour-created-workflow-row",
					title: "Open the Editor",
					content: "Your workflow is ready! <b>Click the workflow row</b> to enter the canvas and start building your logic.",
					placement: "bottom",
					waitForTarget: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-created-workflow-row" }
				}
			);

			return steps;
		}
	},
	async mounted() {
		await this.returnWorkflowSummary();
		window.addEventListener("workflow-tour-start", this.maybeStartListTour);
		this.maybeStartListTour();
	},
	beforeDestroy() {
		window.removeEventListener("workflow-tour-start", this.maybeStartListTour);
	},
	methods: {
		maybeStartListTour() {
			const forced = localStorage.getItem("workflowTourStartRequested") === "true";
			if (!forced) return;

			localStorage.setItem("workflowTourInProgress", "true");
			localStorage.setItem("workflowTourPhase", "list");
			this.workflowsTourVisible = true;
		},
		async persistTourStatus(status) {
			try {
				await httpHelper("RUNTIME", "POST", "security/workflowTour", { status });
			} catch (e) { console.log(e); }
			this.$cookies.set("workflowTourStatus", status, "60d");
		},
		onListTourCompleted() {
			// Completion for full journey is handled on editor phase.
			localStorage.removeItem("workflowTourManualStart");
		},
		onListTourSkipped() {
			localStorage.removeItem("workflowTourInProgress");
			localStorage.removeItem("workflowTourPhase");
			localStorage.removeItem("workflowTourStartRequested");
			localStorage.removeItem("workflowTourManualStart");
			this.workflowsTourVisible = false;
			this.persistTourStatus("skipped");
		},
		handleTourStepChange({ direction }) {
			if (direction === "backward") {
				// Close any UI elements that might have been opened by forward steps
				this.showCreateWorkflowModal = false;
				this.showImportWorkflowModal = false;
			}
		},
		async workflowUpdated(workflow) {
			this.availableWorkflows = this.availableWorkflows.map((each) => {
				if (workflow.id === each.workflowId || workflow.workflowId === each.workflowId) {
					return workflow;
				} else {
					return each;
				}
			});
		},
		workflowDeleted(workflowId) {
			for (let i = this.availableWorkflows.length - 1; i >= 0; i--) {
				const workflow = this.availableWorkflows[i];
				if (workflow.workflowId === workflowId) {
					this.availableWorkflows.splice(i, 1);
				}
			}
		},
		async returnWorkflowSummary() {
			const result = await httpHelper(
				"RUNTIME",
				"GET",
				`workflow/returnSummary?resultsPerPage=${this.pageSize}&page=${
					this.currentPage - 1 //Pagination skip is index 0 so subtract 1 from the current page
				}`,
				{}
			);
			if (!result?.error) {
				this.filters = [...this.filters]; //We do this to trigger loading animation in the workflow item list
				this.availableWorkflows = result.response.workflows;
				this.totalRecords = result.response.totalRecords;
			} else {
				this.$notifyError(this, result.error);
			}
		},
		async sideListEvent(filters) {
			this.filters = filters;
		},
		async workflowCreated(workflow) {
			this.showCreateWorkflowModal = false;
			this.showImportWorkflowModal = false;
			workflow.workflowId = workflow.id;
			this.availableWorkflows = [workflow, ...this.availableWorkflows];
			this.tourWorkflowId = workflow.workflowId;
			// If user created via Enter while tour is on the name step,
			// tell the tour to jump forward to the list-row step.
			window.dispatchEvent(new Event("workflow-tour-workflow-created"));
		},
	},
};
</script>

<style scoped>
.compressedDivider {
	margin-bottom: 0px;
}
</style>
